import { isAdmin } from '../services/adminAuth.js';
import { parseLocalDate } from '../services/dates.js';
import { RevenueService, RevenueMixin } from '../services/revenue.js';

export const Admin = {
  mixins: [RevenueMixin], // Add consistent revenue handling
  data() {
    return {
      isAdmin: false,
      isLoading: true,
      error: null,
      userSearch: "",
      jobSearch: "",
      selectedUserId: null,
      selectedJobId: null,
      unsubscribe: null,
      jobsData: {}, // Raw jobs object from Firebase
      users: {},
      jobsArray: [] // For mixin compatibility
    };
  },
  computed: {
    // Convert jobs object to array for mixin compatibility and expose as jobs property
    jobs() {
      return Object.entries(this.jobsData || {}).map(([id, job]) => ({
        ...job,
        id: id
      }));
    },

    totalUsers() {
      return Object.keys(this.users || {}).length;
    },

    totalJobs() {
      return Object.keys(this.jobsData || {}).length;
    },

    // Updated to use consistent revenue calculation (ONLY paid invoices)
    totalRevenue() {
      return this.formattedRevenue.actual;
    },

    // Total potential revenue (all job quoted prices)
    totalPotentialRevenue() {
      return this.formattedRevenue.total;
    },

    averageJobValue() {
      return this.formattedRevenue.average;
    },

    filteredUsers() {
      if (!this.users) return {};

      const search = this.userSearch.toLowerCase();
      return Object.entries(this.users).reduce((acc, [id, user]) => {
        if (!user) return acc;

        if (
          !search ||
          user.email?.toLowerCase().includes(search) ||
          user.companyName?.toLowerCase().includes(search)
        ) {
          acc[id] = user;
        }
        return acc;
      }, {});
    },

    filteredJobs() {
      if (!this.jobsData) return [];

      const search = this.jobSearch.toLowerCase();
      return Object.entries(this.jobsData)
        .filter(([_, job]) => {
          if (!job) return false;

          return (
            !search ||
            job.customerName?.toLowerCase().includes(search) ||
            job.serviceType?.toLowerCase().includes(search) ||
            job.serviceLocation?.toLowerCase().includes(search)
          );
        })
        .map(([id, job]) => ({ id, ...job }))
        .sort((a, b) => {
          const dateA = a.dateSubmitted
            ? new Date(a.dateSubmitted)
            : new Date(0);
          const dateB = b.dateSubmitted
            ? new Date(b.dateSubmitted)
            : new Date(0);
          return dateB - dateA;
        });
    },

    selectedUser() {
      return this.selectedUserId && this.users
        ? this.users[this.selectedUserId]
        : null;
    },

    selectedJob() {
      return this.selectedJobId && this.jobsData
        ? this.jobsData[this.selectedJobId]
        : null;
    }
  },
  methods: {
    async fetchData() {
      try {
        // Fetch jobs data
        const jobsSnapshot = await firebase
          .database()
          .ref("jobs")
          .once("value");
        this.jobsData = jobsSnapshot.val() || {};

        // Fetch users data
        const usersSnapshot = await firebase
          .database()
          .ref("users")
          .once("value");
        this.users = usersSnapshot.val() || {};
      } catch (err) {
        console.error("Error fetching data:", err);
        this.error = "Failed to load dashboard data";
      }
    },

    // Migration functionality for admin
    async migrateInvoiceData() {
      if (!confirm('⚠️ This will update all incomplete invoice records with missing data. Continue?')) {
        return;
      }

      this.isLoading = true;
      try {
        console.log('🔧 Starting invoice data migration from admin panel...');
        const result = await RevenueService.migrateIncompleteInvoices();

        if (result.success) {
          alert(`✅ Migration completed! Updated ${result.migratedCount} invoices.`);
          await this.fetchData(); // Refresh data
        } else {
          alert(`❌ Migration failed: ${result.error}`);
        }
      } catch (error) {
        console.error('❌ Migration error:', error);
        alert(`❌ Migration failed: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    // Cleanup phantom invoices
    async cleanupPhantomInvoices() {
      if (!confirm('🧹 This will remove duplicate and invalid invoice records. Continue?')) {
        return;
      }

      this.isLoading = true;
      try {
        console.log('🧹 Starting phantom invoice cleanup from admin panel...');
        const result = await RevenueService.cleanupPhantomInvoices();

        if (result.success) {
          alert(`✅ Cleanup completed! Removed ${result.cleanedCount} phantom invoices.`);
          await this.fetchData(); // Refresh data
        } else {
          alert(`❌ Cleanup failed: ${result.error}`);
        }
      } catch (error) {
        console.error('❌ Cleanup error:', error);
        alert(`❌ Cleanup failed: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    setupRealtimeListeners() {
      // Listen for jobs changes
      firebase
        .database()
        .ref("jobs")
        .on("child_added", (snapshot) => {
          this.jobsData = { ...this.jobsData, [snapshot.key]: snapshot.val() };
        });

      firebase
        .database()
        .ref("jobs")
        .on("child_changed", (snapshot) => {
          this.jobsData = { ...this.jobsData, [snapshot.key]: snapshot.val() };
        });

      firebase
        .database()
        .ref("jobs")
        .on("child_removed", (snapshot) => {
          const updatedJobs = { ...this.jobsData };
          delete updatedJobs[snapshot.key];
          this.jobsData = updatedJobs;
        });

      // Listen for users changes
      firebase
        .database()
        .ref("users")
        .on("child_added", (snapshot) => {
          this.users = { ...this.users, [snapshot.key]: snapshot.val() };
        });

      firebase
        .database()
        .ref("users")
        .on("child_changed", (snapshot) => {
          this.users = { ...this.users, [snapshot.key]: snapshot.val() };
        });

      firebase
        .database()
        .ref("users")
        .on("child_removed", (snapshot) => {
          const updatedUsers = { ...this.users };
          delete updatedUsers[snapshot.key];
          this.users = updatedUsers;
        });
    },

    cleanupListeners() {
      firebase.database().ref("jobs").off();
      firebase.database().ref("users").off();
    },

    async checkAdminStatus() {
      try {
        const user = firebase.auth().currentUser;
        if (!user) {
          this.isAdmin = false;
          return;
        }

        this.user = user; // Store user reference

        this.isAdmin = await isAdmin();

        // If admin, fetch data and setup listeners
        if (this.isAdmin) {
          await this.fetchData();
          this.setupRealtimeListeners();
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        this.isAdmin = false;
      } finally {
        this.isLoading = false; // Make sure to set loading to false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = parseLocalDate(dateStr);
      if (!date) {
        console.error("Error formatting date:", dateStr);
        return dateStr;
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    getUserJobCount(userId) {
      if (!userId) return 0;
      // Count jobs where userId matches
      return Object.values(this.jobsData || {}).filter(
        (job) => job.userId === userId
      ).length;
    },

    getUserRevenue(userId) {
      if (!userId) return this.formatCurrency(0);

      const total = Object.values(this.jobsData || {})
        .filter((job) => job.userId === userId)
        .reduce((sum, job) => {
          if (!job?.quotedPrice) return sum;
          const price = parseFloat(job.quotedPrice.replace(/[$,]/g, ""));
          return sum + (isNaN(price) ? 0 : price);
        }, 0);

      return this.formatCurrency(total);
    },

    getUserJobs(userId) {
      if (!userId || !this.users[userId]?.jobs) return [];

      return Object.keys(this.users[userId].jobs)
        .map((jobId) => {
          const job = this.jobsData[jobId];
          return job ? { id: jobId, ...job } : null;
        })
        .filter((job) => job !== null)
        .sort((a, b) => {
          const dateA = a.dateSubmitted
            ? new Date(a.dateSubmitted)
            : new Date(0);
          const dateB = b.dateSubmitted
            ? new Date(b.dateSubmitted)
            : new Date(0);
          return dateB - dateA;
        });
    },

    viewUserDetails(userId) {
      if (!userId) return;
      this.selectedUserId = userId;
      const userModal = new bootstrap.Modal("#userDetailsModal");
      userModal.show();
    },

    viewJobDetails(jobId) {
      if (!jobId) return;
      this.selectedJobId = jobId;
      const jobModal = new bootstrap.Modal("#jobDetailsModal");
      jobModal.show();
    },

    handleError(error, message) {
      console.error(error);
      this.error = message || "An error occurred";
    }, // Helper method to handle errors

    navigateToProfile(userId) {
      // Using Vue Router
      this.$router.push(`/profile/${userId}`);
      // Or for hash-based routing
      // this.$router.push(`#/admin/users/${userId}`);
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(value);
    }
  },
  async mounted() {
    try {
      this.isLoading = true; // Set loading state

      this.unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
        if (user) {
          this.user = user;
          await this.checkAdminStatus();
        } else {
          this.isAdmin = false;
          this.cleanupListeners();
          this.jobs = {};
          this.users = {};
        }
      });
    } catch (error) {
      this.handleError(error, "Failed to initialize admin dashboard");
    } finally {
      this.isLoading = false;
    }
  },
  unmounted() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.cleanupListeners();
  },
  template: "#admin"
};
